import React, { useContext, useMemo, useState } from 'react';
import { ModuleBase, UserContext } from '@/components';
import { ModuleBaseModel, ModuleBasePropsModel } from '@/types';
import { useTranslations } from 'next-intl';
import { formatRemainingTime } from '@/utils';
import classNames from 'classnames';
import { ProgressBar } from '@tremor/react';
import ClockIcon from '@/assets/clockLight.svg';
import { saveVote } from '@/api';

type PollModel = {
  id: string;
  question: string;
  totalVotes: number;
  endDate: string;
  options: { id: string; text: string; votes?: number }[];
  userVoted: number;
};

type PollingModuleModel = ModuleBaseModel<
  {
    polls: PollModel[];
  } & ModuleBasePropsModel
>;

const PollingModule = (data: PollingModuleModel) => {
  const {
    props: { polls },
  } = data;

  if (!polls || polls.length === 0) return null;

  return (
    <ModuleBase data={data} noPadding noModuleBaseColor>
      <div className="container flex h-full flex-col">
        <div className="bg-mint px-8 py-8 xl:px-12">
          <div className="text-4xl font-light tracking-[1.25px] text-white md:text-[22px] lg:text-4xl">
            Live Polling
          </div>
        </div>

        {polls.length > 0 &&
          polls.map((poll, index) => <PollingModuleContent key={poll.id} data={poll} dark={index % 2 === 0} />)}
      </div>
    </ModuleBase>
  );
};

const PollingModuleContent = ({ data: originalData, dark }: { data: PollModel; dark: boolean }) => {
  const [value, setValue] = useState<string>(
    originalData.userVoted !== -1 ? originalData.options[originalData.userVoted]?.id : '',
  );
  const [data, setData] = useState<PollModel>(originalData);
  const [animate, setAnimate] = useState<boolean>(false);

  const user = useContext(UserContext);
  const t = useTranslations();

  const submit = async () => {
    if (user?.userId) {
      setAnimate(true);
      const response = await saveVote(data.id, value, user?.userId);

      setData((prev) => {
        const updatedOptions = prev.options.map((option) =>
          option.id === value ? { ...option, votes: (option.votes || 0) + 1 } : option,
        );
        return { ...prev, options: updatedOptions, totalVotes: response.TotalVotes, userVoted: response.UserVoteIndx };
      });

      setTimeout(() => {
        setAnimate(false);
      }, 700);
    }
  };

  const voteClosed = useMemo(
    () => !user?.userId || data.userVoted !== -1 || new Date(data.endDate).getTime() < new Date().getTime(),
    [data.endDate, data.userVoted, user?.userId],
  );

  const totalVotesSafe = data.totalVotes > 0 ? data.totalVotes : 0;

  return (
    <div
      className={classNames(
        `polling-content flex flex-col justify-center border-x px-8 py-3 xl:px-12`,
        dark ? 'module-base-dark border-mint' : 'module-base-light bg-mint border-transparent',
        voteClosed ? 'gap-2' : 'gap-4',
      )}
    >
      {new Date(data.endDate).getTime() < new Date().getTime() && (
        <div className="color-from-bg text-xs font-light">Last months results</div>
      )}

      <h2 className="color-from-bg text-2xl leading-[28px] font-light -tracking-[0.5px]">{data.question}</h2>

      {voteClosed ? (
        <div className="flex flex-col gap-0.5">
          {data.options.map((option) => {
            const votePercentage = totalVotesSafe ? ((option.votes || 0) * 100) / totalVotesSafe : 0;
            return (
              <div
                key={option.id}
                className={classNames(
                  'flex flex-col gap-1',
                  voteClosed && value && value !== option.id && 'opacity-50',
                )}
              >
                <div className="color-from-bg text-sm font-light">{option.text}</div>
                <div className="relative">
                  <ProgressBar
                    value={animate ? 0 : votePercentage}
                    showAnimation
                    className={classNames(
                      '[&_.tremor-ProgressBar-progressBarWrapper]:h-7 [&_.tremor-ProgressBar-progressBarWrapper]:rounded-full [&_.tremor-ProgressBar-progressBarWrapper]:border',
                      '[&_.tremor-ProgressBar-progressBar]:rounded-full [&_.tremor-ProgressBar-progressBar]:transition-all [&_.tremor-ProgressBar-progressBar]:duration-500',
                      dark
                        ? '[&_.tremor-ProgressBar-progressBar]:bg-mint [&_.tremor-ProgressBar-progressBarWrapper]:border-mint'
                        : '[&_.tremor-ProgressBar-progressBar]:bg-white [&_.tremor-ProgressBar-progressBarWrapper]:border-white',
                    )}
                  />
                  <div className="absolute top-0 right-0 flex h-full items-center pr-2">
                    <span
                      className={classNames(
                        'text-sm',
                        votePercentage === 100
                          ? dark
                            ? 'text-white'
                            : 'text-mint'
                          : dark
                            ? 'text-mint'
                            : 'text-white',
                      )}
                    >
                      {votePercentage.toFixed(0)}%
                    </span>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        <>
          {data.options.map((option) => (
            <div key={option.id} className="flex">
              <label className="flex w-auto cursor-pointer items-center gap-2">
                <input
                  type="radio"
                  name={`poll-${data.id}`}
                  id={`poll-${data.id}-${option.id}`}
                  disabled={data.userVoted !== -1}
                  value={option.id}
                  checked={value === option.id}
                  onChange={() => setValue(option.id)}
                  className="peer hidden"
                />

                <div
                  className={classNames(
                    dark ? 'peer-checked:bg-mint' : 'peer-checked:bg-white',
                    'border-from-bg flex h-4.5 w-4.5 shrink-0 items-center justify-center rounded-full border transition-colors',
                  )}
                />

                <span className="color-from-bg text-sm">{option.text}</span>
              </label>
            </div>
          ))}
          <div>
            <button
              className={classNames(
                'btn secondary btn-from-bg border !py-2 !text-sm !font-light !tracking-normal',
                dark && '!border-black',
              )}
              disabled={!value}
              onClick={submit}
            >
              {t('general.$submit')}
            </button>
          </div>
        </>
      )}

      <div className={classNames('color-from-bg flex gap-1 text-xs font-light', voteClosed && 'mt-2')}>
        <ClockIcon className="fill-from-bg h-3.5 w-3.5" />
        {formatRemainingTime(new Date(data.endDate))}
      </div>
    </div>
  );
};

export default PollingModule;
